/**
 * Is this browser on a connection where data should be spared? True when
 * the person switched on the browser's data saver, or the browser reports
 * a 2G or 3G class connection. Browsers that report nothing (Safari,
 * Firefox) answer false: nothing is held back there.
 */
type Reported = { saveData?: boolean; effectiveType?: string };

const SLOW = new Set(["slow-2g", "2g", "3g"]);

export const isSlowConnection = (): boolean => {
  const connection = (globalThis.navigator as (Navigator & { connection?: Reported }) | undefined)?.connection;

  if (!connection) return false;
  return Boolean(connection.saveData) || SLOW.has(connection.effectiveType ?? "");
};

/** "184 MB", "3.2 MB", "640 KB". */
export const fileSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`
    : `${Math.ceil(bytes / 1024)} KB`;

/**
 * What to say before a heavy file loads on a slow connection, from the
 * task's measured file (``meta.media``: kind, size, heavy). Nothing when
 * the file is not heavy, was never measured, or the connection is fine.
 */
export const heavyFileNotice = (media?: { kind?: string; size?: number; heavy?: boolean } | null): string | null => {
  if (!media?.heavy || !isSlowConnection()) return null;
  const what = media.kind === "video" ? "video" : media.kind === "audio" ? "recording" : "file";

  return media.size
    ? `This ${what} is ${fileSize(media.size)}. On this connection it will take a while to load.`
    : `This ${what} is large. On this connection it will take a while to load.`;
};
